<template>
  <div
    id="toast-warning"
    class="fixed z-[1052] rounded-md flex items-center text-body shadow-xs"
    :class="[topCenter || topRight || 'left-[50%] -translate-x-[50%] p-3 top-2 max-w-2xl w-full max-w-lg', topRight, topCenter, getColor() ]"
    role="alert"
  >
    <!-- Show icon separately when no title -->
    <div
      v-if="!props.title"
      class="inline-flex items-center justify-center text-fg-warning bg-warning-soft rounded"
    >
      <i class="text-2xl text-[#fff]" :class="resolvedIcon()"></i>
      <span class="sr-only">Warning icon</span>
    </div>

    <div class="text-[#fff]" :class="{ 'ms-0': props.title }">
      <!-- Title with icon when title is available -->
      <div v-if="props.title" class="font-semibold flex" :class="topCenter ? 'text-sm items-center' : 'text-lg items-start' ">
        <div
          class="inline-flex items-center justify-center shrink-0 w-[24px] h-[24px] text-fg-warning bg-warning-soft rounded mr-2"
        >
          <i class="w-[24px] text-2xl text-[#fff] h-[24px]" :class="resolvedIcon()"></i>
          <span class="sr-only">Warning icon</span>
        </div>
        {{ props.title }}
      </div>
      <div class="text-sm pl-2 font-normal whitespace-pre-line" :class="{ 'ml-8': props.title }">
        {{ props.message }}
      </div>
    </div>

    <button
      id="order-wizard-alert-close-button"
      type="button"
      class="ms-auto flex items-center justify-center text-body hover:text-heading bg-transparent hover:bg-neutral-secondary-medium focus:ring-4 focus:ring-neutral-tertiary font-medium leading-5 rounded text-sm h-8 w-8 focus:outline-none"
      data-dismiss-target="#toast-warning"
      aria-label="Close"
      @click="$emit('close')"
    >
      <span class="sr-only">Close</span>
      <i class="ri-close-line text-[#fff] text-2xl"></i>
    </button>

    <!-- Progress bar: black background with 40% opacity, white filling -->
    <div
      v-if="props.showProgress"
      :class="getColor()"
      class="absolute bottom-0 left-0 w-full h-1 rounded-b overflow-hidden"
    >
      <div class="h-full bg-[#000000]/40" :style="progressStyle"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  title: { type: String, default: "" },
  message: { type: String, default: "" },
  type: { type: String, default: "info" },
  duration: { type: Number, default: 5000 }, // ms
  showProgress: { type: Boolean, default: true }, // show progress bar or not
  icon: { type: String, default: "" },
  topRight: {type: String, default: "" },
  topCenter: {type: String, default: "" }
});

const emit = defineEmits(["close"]);

// progress value (0..100)
const progress = ref(100);

// timers so we can clear them on unmount
let startAnimTimer = null;
let autoCloseTimer = null;

const progressStyle = computed(() => {
  // width is progress%, transition duration equals props.duration
  return {
    width: progress.value + "%",
    transition: `width ${props.duration}ms linear`,
  };
});

function getColor() {
  switch (props.type) {
    case "error":
      return "bg-[#C61E1F]";
    case "warning":
      return "bg-[#F7C053]";
    case "success":
      return "bg-[#248726]";
    default:
      return "bg-[#F7C053]";
  }
}

function getIcon() {
  switch (props.type) {
    case "error":
      return "ri-error-warning-line";
    case "warning":
      return "ri-alert-line";
    case "success":
      return "ri-check-line";
    default:
      return "ri-alert-line";
  }
}

function resolvedIcon() {
  return props.icon || getIcon();
}

onMounted(async () => {
  // ensure initial width is 100% in DOM, then trigger to 0 so the transition runs
  if (props.showProgress) {
    // wait for DOM to paint initial state
    await nextTick();

    // tiny delay to ensure browser applies initial style
    startAnimTimer = setTimeout(() => {
      progress.value = 0;
    }, 20);
  }

  // auto-close after duration: emit 'close' like your button does
  autoCloseTimer = setTimeout(() => {
    emit("close");
  }, props.duration);
});

onBeforeUnmount(() => {
  if (startAnimTimer) clearTimeout(startAnimTimer);
  if (autoCloseTimer) clearTimeout(autoCloseTimer);
});
</script>
